import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { Container, Box, Typography, Paper, LinearProgress, Alert, Button, Stack } from '@mui/material';
import { useModelStatus } from './services/modelService';
import { useModelDownload } from './services/modelDownload';
import { useSyncStatus } from './services/sync';
import { useStorageMonitor } from './services/storageMonitor';
import { DocumentProcessor } from './components';
import { db } from './services/database';
import './App.css';

// Create dark theme
const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#1976d2',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
  },
});

function App() {
  const [showProcessor, setShowProcessor] = useState(false);
  const [dbInitialized, setDbInitialized] = useState(false);
  const modelStatus = useModelStatus();
  const { progress: downloadProgress, isDownloading } = useModelDownload();
  const syncStatus = useSyncStatus();
  const { usage, alert: storageAlert } = useStorageMonitor();

  // Initialize database on mount
  useEffect(() => {
    const initDb = async () => {
      try {
        await db.initialize();
        setDbInitialized(true);
        console.log('Database initialized successfully');
      } catch (error) {
        console.error('Failed to initialize database:', error);
      }
    };
    initDb();
  }, []);

  if (showProcessor) {
    return (
      <ThemeProvider theme={darkTheme}>
        <CssBaseline />
        <DocumentProcessor />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            ExAn Platform
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Enterprise-Grade Offline-First AI Data Extraction & Analytics
          </Typography>

          {/* Model Download Status */}
          {isDownloading && (
            <Paper sx={{ p: 3, my: 3 }}>
              <Typography variant="h6" gutterBottom>
                Downloading AI Models
              </Typography>
              {downloadProgress.map((model) => (
                <Box key={model.modelName} sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {model.modelName}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={model.percentage} 
                    sx={{ mt: 1 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {model.percentage.toFixed(1)}% - {model.status}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {/* Storage Alert */}
          {storageAlert && (
            <Alert 
              severity={storageAlert.level === 'critical' ? 'error' : storageAlert.level}
              sx={{ mb: 3 }}
              action={
                storageAlert.action && (
                  <button onClick={storageAlert.action}>
                    Take Action
                  </button>
                )
              }
            >
              {storageAlert.message}
            </Alert>
          )}

          {/* System Status */}
          <Paper sx={{ p: 3, my: 3 }}>
            <Typography variant="h6" gutterBottom>
              System Status
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Database Status
              </Typography>
              <Typography>
                {dbInitialized ? 'Ready' : 'Initializing...'}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Model Status
              </Typography>
              <Typography>
                {modelStatus?.initialized ? 
                  `Initialized (${modelStatus.executionProvider.toUpperCase()})` : 
                  'Initializing...'}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Network Status
              </Typography>
              <Typography>
                {syncStatus?.online ? 'Online' : 'Offline'}
                {syncStatus?.pendingItems ? ` (${syncStatus.pendingItems} pending sync)` : ''}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Storage Usage
              </Typography>
              {usage && (
                <Typography>
                  {(usage.totalSizeGB * 1024).toFixed(1)} MB used
                  {usage.documents.count > 0 && ` (${usage.documents.count} documents)`}
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Quick Actions */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={() => setShowProcessor(true)}
                disabled={!dbInitialized}
              >
                Open Document Processor
              </Button>
              <Button variant="outlined" disabled>
                Batch Processing
              </Button>
              <Button variant="outlined" disabled>
                Analytics Dashboard
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Container>
    </ThemeProvider>
  );
}

export default App;
